'use client';

import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import ExpandMore from '@mui/icons-material/ExpandMore';

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    xxl: true;
    xxxl: true;
  }

  interface TypeBackground {
    darkBlue: string;
  }
}

const BORDER_RADIUS = 4;
const SPACING = 20;

export const PURPLE_COLOR = '#6359CB';
export const GREEN_COLOR = '#5EB18F';
// Pale tint of GREEN_COLOR used as a *selected surface* (parameter cards).
// Deliberately not wired to palette.primary.light, which is consumed as a
// foreground/border colour elsewhere and would lose contrast if lightened.
export const GREEN_LIGHT_COLOR = '#DDF0E7';
export const BLUE_COLOR = '#2196F3';
export const ORANGE_COLOR = '#DC900D';
export const RED_COLOR = '#EE6645';
export const GRAY_COLOR = '#F5F5F5';
const WHITE_COLOR = '#FFFFFF';
export const BLACK_COLOR = '#000000';
export const YELLOW_COLOR = '#DC900D';

const DIVIDER_COLOR = '#E1F0E6';
const BG_PAPER_COLOR = '#F5FCF7';
const BG_DARKBLUE_COLOR = '#002039';

const SHADOW_COLOR_1 = 'rgba(0, 0, 0, 0.05)';
const SHADOW_COLOR_2 = 'rgba(0, 0, 0, 0.04)';
const SHADOW_COLOR_3 = 'rgba(0, 0, 0, 0.03)';

const theme = createTheme({
  cssVariables: true,
  palette: {
    contrastThreshold: 4.5,
    common: {
      white: WHITE_COLOR,
      black: BLACK_COLOR,
    },
    primary: {
      main: GREEN_COLOR,
      contrastText: WHITE_COLOR,
    },
    secondary: {
      main: PURPLE_COLOR,
    },
    divider: DIVIDER_COLOR,
    background: {
      default: WHITE_COLOR,
      paper: BG_PAPER_COLOR,
      darkBlue: BG_DARKBLUE_COLOR,
    },
    text: {
      primary: '#002039',
      secondary: '#6E6E6E',
    },
    info: {
      main: BLUE_COLOR,
    },
    warning: {
      main: ORANGE_COLOR,
    },
    error: {
      main: RED_COLOR,
    },
    success: {
      main: GREEN_COLOR,
    },
  },
  shape: {
    borderRadius: BORDER_RADIUS,
  },
  spacing: SPACING,
  shadows: [
    'none',
    `0px 2px 1px -1px ${SHADOW_COLOR_1},0px 1px 1px 0px ${SHADOW_COLOR_2},0px 1px 3px 0px ${SHADOW_COLOR_3}`,
    `0px 3px 1px -2px ${SHADOW_COLOR_1},0px 2px 2px 0px ${SHADOW_COLOR_2},0px 1px 5px 0px ${SHADOW_COLOR_3}`,
    `0px 3px 3px -2px ${SHADOW_COLOR_1},0px 3px 4px 0px ${SHADOW_COLOR_2},0px 1px 8px 0px ${SHADOW_COLOR_3}`,
    `0px 2px 4px -1px ${SHADOW_COLOR_1},0px 4px 5px 0px ${SHADOW_COLOR_2},0px 1px 10px 0px ${SHADOW_COLOR_3}`,
    `0px 3px 5px -1px ${SHADOW_COLOR_1},0px 5px 8px 0px ${SHADOW_COLOR_2},0px 1px 14px 0px ${SHADOW_COLOR_3}`,
    `0px 3px 5px -1px ${SHADOW_COLOR_1},0px 6px 10px 0px ${SHADOW_COLOR_2},0px 1px 18px 0px ${SHADOW_COLOR_3}`,
    `0px 4px 5px -2px ${SHADOW_COLOR_1},0px 7px 10px 1px ${SHADOW_COLOR_2},0px 2px 16px 1px ${SHADOW_COLOR_3}`,
    `0px 5px 5px -3px ${SHADOW_COLOR_1},0px 8px 10px 1px ${SHADOW_COLOR_2},0px 3px 14px 2px ${SHADOW_COLOR_3}`,
    `0px 5px 6px -3px ${SHADOW_COLOR_1},0px 9px 12px 1px ${SHADOW_COLOR_2},0px 3px 16px 2px ${SHADOW_COLOR_3}`,
    `0px 6px 6px -3px ${SHADOW_COLOR_1},0px 10px 14px 1px ${SHADOW_COLOR_2},0px 4px 18px 3px ${SHADOW_COLOR_3}`,
    `0px 6px 7px -4px ${SHADOW_COLOR_1},0px 11px 15px 1px ${SHADOW_COLOR_2},0px 4px 20px 3px ${SHADOW_COLOR_3}`,
    `0px 7px 8px -4px ${SHADOW_COLOR_1},0px 12px 17px 2px ${SHADOW_COLOR_2},0px 5px 22px 4px ${SHADOW_COLOR_3}`,
    `0px 7px 8px -4px ${SHADOW_COLOR_1},0px 13px 19px 2px ${SHADOW_COLOR_2},0px 5px 24px 4px ${SHADOW_COLOR_3}`,
    `0px 7px 9px -4px ${SHADOW_COLOR_1},0px 14px 21px 2px ${SHADOW_COLOR_2},0px 5px 26px 4px ${SHADOW_COLOR_3}`,
    `0px 8px 9px -5px ${SHADOW_COLOR_1},0px 15px 22px 2px ${SHADOW_COLOR_2},0px 6px 28px 5px ${SHADOW_COLOR_3}`,
    `0px 8px 10px -5px ${SHADOW_COLOR_1},0px 16px 24px 2px ${SHADOW_COLOR_2},0px 6px 30px 5px ${SHADOW_COLOR_3}`,
    `0px 8px 11px -5px ${SHADOW_COLOR_1},0px 17px 26px 2px ${SHADOW_COLOR_2},0px 6px 32px 5px ${SHADOW_COLOR_3}`,
    `0px 9px 11px -5px ${SHADOW_COLOR_1},0px 18px 28px 2px ${SHADOW_COLOR_2},0px 7px 34px 6px ${SHADOW_COLOR_3}`,
    `0px 9px 12px -6px ${SHADOW_COLOR_1},0px 19px 29px 2px ${SHADOW_COLOR_2},0px 7px 36px 6px ${SHADOW_COLOR_3}`,
    `0px 10px 13px -6px ${SHADOW_COLOR_1},0px 20px 31px 3px ${SHADOW_COLOR_2},0px 8px 38px 7px ${SHADOW_COLOR_3}`,
    `0px 10px 13px -6px ${SHADOW_COLOR_1},0px 21px 33px 3px ${SHADOW_COLOR_2},0px 8px 40px 7px ${SHADOW_COLOR_3}`,
    `0px 10px 14px -6px ${SHADOW_COLOR_1},0px 22px 35px 3px ${SHADOW_COLOR_2},0px 8px 42px 7px ${SHADOW_COLOR_3}`,
    `0px 11px 14px -7px ${SHADOW_COLOR_1},0px 23px 36px 3px ${SHADOW_COLOR_2},0px 9px 44px 8px ${SHADOW_COLOR_3}`,
    `0px 11px 15px -7px ${SHADOW_COLOR_1},0px 24px 38px 3px ${SHADOW_COLOR_2},0px 9px 46px 8px ${SHADOW_COLOR_3}`,
  ],
  breakpoints: {
    values: {
      xxxl: 1920,
      xxl: 1536,
      xl: 1440,
      lg: 1280,
      md: 1024,
      sm: 600,
      xs: 0,
    },
  },
  typography: {
    fontFamily: 'var(--font-regular)',
    fontWeightBold: 700,
    fontWeightMedium: 500,
    fontWeightRegular: 400,
    button: {
      textTransform: 'none',
    },
  },
  components: {
    MuiAutocomplete: {
      defaultProps: {
        popupIcon: <ExpandMore />,
      },
    },
    MuiCircularProgress: {
      defaultProps: {
        'aria-label': 'Loading',
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: BG_PAPER_COLOR,
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: 'outlined',
        IconComponent: ExpandMore,
        displayEmpty: true,
      },
    },
    MuiTextField: {
      defaultProps: {
        margin: 'normal',
        fullWidth: true,
        rows: 3,
        variant: 'standard',
        slotProps: {
          select: {
            IconComponent: ExpandMore,
          },
          inputLabel: {
            shrink: true,
          },
        },
      },
      styleOverrides: {
        root: {
          '& .MuiFormLabel-root': {
            color: GREEN_COLOR,
            fontSize: 18,
          },
          '& .MuiInputBase-root': {
            border: '1px solid',
            backgroundColor: WHITE_COLOR,
            borderColor: '#BBDAC9',
            borderRadius: BORDER_RADIUS * 0.5,
            padding: `${SPACING * 0.25}px ${SPACING * 0.5}px`,
            '&:focus': {
              borderColor: GREEN_COLOR,
            },
          },
          '& .MuiAutocomplete-endAdornment': {
            right: SPACING * 0.5,
          },
          '& .MuiInput-root': {
            marginTop: SPACING * 1.25,
          },
          '& .MuiInput-root:before': {
            display: 'none',
          },
          '& .MuiInput-root:after': {
            display: 'none',
          },
        },
      },
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
    },
    MuiGrid: {
      defaultProps: {
        columns: 8,
        spacing: 1,
        sx: {
          justifyContent: 'center',
        },
      },
    },
    MuiStack: {
      defaultProps: {
        direction: 'row',
        spacing: 1,
      },
    },
    MuiContainer: {
      defaultProps: {
        maxWidth: false,
      },
      styleOverrides: {
        root: {
          padding: SPACING,
          gap: SPACING,
          display: 'flex',
          flexDirection: 'column',
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          position: 'relative',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          },
          '&:active': {
            transform: 'translateY(0)',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            opacity: 0,
            transition: 'opacity 0.2s ease-in-out',
            background:
              'radial-gradient(circle, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 70%)',
            pointerEvents: 'none',
          },
          '&:active::after': {
            opacity: 0.3,
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
      },
      styleOverrides: {
        root: {
          whiteSpace: 'nowrap',
        },
        outlined: {
          border: '2px solid',
          '&.MuiButton-colorPrimary': {
            borderColor: 'var(--mui-palette-primary-main)',
            color: 'var(--mui-palette-primary-main)',
          },
          '&.MuiButton-colorSecondary': {
            borderColor: 'var(--mui-palette-secondary-main)',
            color: 'var(--mui-palette-secondary-main)',
          },
          '&:disabled': {
            border: '2px solid',
          },
          '&:active': {
            border: '2px solid',
          },
          '&:hover': {
            border: '2px solid',
          },
        },
        contained: {
          boxShadow: `0px 1px 3px rgba(0, 0, 0, 0.1)`,
        },
      },
    },
    MuiPopover: {
      defaultProps: {
        elevation: 4,
      },
      styleOverrides: {
        root: {
          '& .MuiPopover-paper': {
            boxShadow: '0px 1px 5px rgba(0, 0, 0, 0.1)', // Added subtle shadow
            backgroundColor: WHITE_COLOR,
            borderRadius: BORDER_RADIUS,
          },
        },
      },
    },
    MuiCard: {
      defaultProps: {
        variant: 'elevation',
        elevation: 4,
      },
      styleOverrides: {
        root: {
          width: '100%',
          height: 'fit-content',
          borderRadius: BORDER_RADIUS,
          bgcolor: WHITE_COLOR,
          padding: SPACING * 1.5,
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        variant: 'elevation',
        elevation: 4,
      },
      styleOverrides: {
        root: {
          borderRadius: 0,
          border: 0,
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        variant: 'elevation',
        elevation: 4,
      },
      styleOverrides: {
        root: {
          backgroundColor: WHITE_COLOR,
          borderRadius: BORDER_RADIUS,
        },
      },
    },
  },
});

const responsiveTheme = responsiveFontSizes(theme);

export default responsiveTheme;
